import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import api from '@/lib/api'; // Import the api client directly
import { QUERY_KEYS, QUERY_CONFIG } from '@/constants/queryKeys';
import { useAuth } from '@/contexts/AuthContext'; // Import useAuth

// Define DTOs directly in the hook file or a shared types file
// For simplicity here, defining them in the hook file.

export interface GuardianAddressDto {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface GuardianProfileDto {
  id: string;
  relationship: string;
  email?: string;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  phoneNumber?: string | null;
  profileImage?: string | null;
  address?: GuardianAddressDto | null;
}

export interface GuardianChildDto {
  studentUserId?: string;
  id: string; // This is the studentId from the backend Student record
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  gender?: string | null;
  dateOfBirth?: Date | null;
  profileImage?: string | null;
  enrollmentNumber: string;
  status: string; // Student general status
  apaarNumber?: string | null;

  // Include institute information for the student
  instituteId?: string;
  instituteName?: string;

  // New fields from UserDetails for exact UI match (from backend DTO)
  nationality?: string | null;
  aadhaarNumber?: string | null;

  // Contact info for the student (from backend DTO)
  email?: string | null; // Student's own email
  phoneNumber?: string | null; // Student's own phone from UserDetails

  // Student's addresses (from backend DTO)
  residentialAddress?: GuardianAddressDto | null;
  permanentAddress?: GuardianAddressDto | null;

  // Guardian-student relationship specific
  isPrimaryGuardianContact: boolean;
  isEmergencyContactForStudent: boolean;

  // Academic details from enrollment
  rollNumber?: string | null;
  enrollmentStatus?: string | null; // ENROLLED, PROMOTED etc.
  enrollmentDate?: Date | null;
  className?: string | null;
  sectionName?: string | null;
  streamName?: string | null;
  programmeName?: string | null;
  classId?: string | null;
  academicSessionName?: string | null;
  academicSessionStartDate?: string | null;
  academicSessionEndDate?: string | null;
  guardians?: {
    id: string;
    name: string;
    relation: string;
    email?: string | null;
    contact?: string | null;
    profileImage?: string | null;
    address?: GuardianAddressDto | null;
    isPrimaryGuardian: boolean;
    isEmergencyContact: boolean;
  }[];
}

// Type for the options parameter, excluding queryKey and queryFn which are set by the hook
type CustomQueryOptions<
  TData,
  TError = Error,
  TQueryKey extends readonly unknown[] = string[],
> = Omit<
  UseQueryOptions<TData, TError, TData, TQueryKey>,
  'queryKey' | 'queryFn'
>;

/**
 * Custom hook to fetch the guardian's profile data.
 */
export const useGuardianProfile = (
  options?: CustomQueryOptions<GuardianProfileDto, Error, [string]>,
) => {
  return useQuery<GuardianProfileDto, Error, GuardianProfileDto, [string]>({
    queryKey: [QUERY_KEYS.GUARDIAN_PROFILE],
    queryFn: async () => {
      const response = await api.get<GuardianProfileDto>('/parent/profile');
      return response.data;
    },
    ...QUERY_CONFIG.DEFAULT,
    ...options,
  });
};

/**
 * Custom hook to fetch the list of children for the logged-in guardian.
 */
export const useGuardianChildren = (
  options?: CustomQueryOptions<GuardianChildDto[], Error, [string]>,
) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth(); // Get isAuthenticated and isLoading from useAuth

  return useQuery<GuardianChildDto[], Error, GuardianChildDto[], [string]>({
    queryKey: [QUERY_KEYS.GUARDIAN_CHILDREN],
    queryFn: async () => {
      const response = await api.get<GuardianChildDto[]>('/parent/children');
      return response.data;
    },
    ...QUERY_CONFIG.DEFAULT,
    enabled: isAuthenticated && !isAuthLoading, // Only fetch when authenticated and auth is not loading
    ...options,
  });
};

/**
 * Custom hook to fetch the details for a specific student.
 */
export const useStudentDetails = (
  studentId: string,
  options?: CustomQueryOptions<GuardianChildDto, Error, [string, string]>,
) => {
  return useQuery<GuardianChildDto, Error, GuardianChildDto, [string, string]>({
    queryKey: [QUERY_KEYS.STUDENT_DETAILS, studentId], // Include studentId in the queryKey
    queryFn: async () => {
      const response = await api.get<GuardianChildDto>(
        `/parent/children/${studentId}`,
      );
      return response.data;
    },
    ...QUERY_CONFIG.DEFAULT,
    enabled: !!studentId, // Only run the query if studentId is available
    ...options,
  });
};
